import React, { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import config from "../common/Config";
import { toast } from "sonner";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Grid2,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Autocomplete,
  InputAdornment,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { format, parse } from "date-fns";
import { Iconify } from "../Minimal/src/components/iconify/iconify";
import { useNavigate } from "react-router-dom";
import QuantityControl from "../common/QuantityControl";
import "../common/commonStyles.css";

const PRODUCT_OPTION_LIMIT = 100;
const PRODUCT_OPTION_INITIAL = 50;

const normalizeReturnUnit = (type) => {
  const value = String(type ?? "").trim().toLowerCase();
  if (value === "piece" || value === "pieces" || value === "p") return "piece";
  return "box";
};

const displayOrderType = (type) => {
  const unit = normalizeReturnUnit(type);
  return unit === "piece" ? "Piece" : "Box";
};

const normalizeSplit = (split) => {
  const value = String(split ?? "0").trim();
  if (value === "1") return "1";
  if (value === "2") return "2";
  return "0";
};

const defaultReturnTypeFromSplit = (split) => {
  const s = normalizeSplit(split);
  if (s === "1") return "piece";
  return "box";
};

const parseMoney = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const pricesForReturnType = (item, returnType) => {
  const isPiece = normalizeReturnUnit(returnType) === "piece";
  const stdPrice = isPiece ? item.piece_price : item.box_price;
  const offerPrice = isPiece ? item.offer_piece_price : item.offer_box_price;
  return {
    std_price: stdPrice,
    offer_price: parseMoney(offerPrice) > 0 ? offerPrice : stdPrice,
  };
};

const formatMoney = (value) => `£${parseMoney(value).toFixed(2)}`;

const normalizeDiscountType = (type) => {
  const value = String(type ?? "percentage").trim().toLowerCase();
  if (value === "amount" || value === "fixed" || value === "£") return "amount";
  return "percentage";
};

const calculateRowTotal = (row) => {
  const qty = Number(row.return_qty) || 0;
  const offerPrice = parseMoney(row.offer_price);
  const lineSubtotal = offerPrice * qty;
  const discount = parseMoney(row.item_discount);
  if (discount <= 0) return lineSubtotal;

  const discountType = normalizeDiscountType(row.item_discount_type);
  if (discountType === "percentage") {
    const pct = Math.min(Math.max(discount, 0), 100);
    return Math.max(0, lineSubtotal * (1 - pct / 100));
  }

  return Math.max(0, (offerPrice - discount) * qty);
};

const validateRowDiscount = (row, discountValue, discountType) => {
  const parsed = parseMoney(discountValue);
  if (parsed <= 0) return true;

  const unitPrice = parseMoney(row.offer_price);
  const type = normalizeDiscountType(discountType);

  if (type === "percentage") {
    if (parsed > 100) {
      toast.error("Discount percentage cannot exceed 100");
      return false;
    }
    return true;
  }

  if (unitPrice > 0 && parsed > unitPrice) {
    toast.error(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
    return false;
  }

  return true;
};

const resolveProductPrices = (item) => {
  const boxPrice = parseMoney(item.stand_box_price ?? item.standard_box_price ?? 0);
  const piecePrice = parseMoney(
    item.stand_piece_price ?? item.standard_piece_price ?? boxPrice
  );
  const offerBoxRaw = parseMoney(item.offer_box_price);
  const offerPieceRaw = parseMoney(item.offer_piece_price);
  const offerBox = offerBoxRaw > 0 ? offerBoxRaw : boxPrice;
  const offerPiece = offerPieceRaw > 0 ? offerPieceRaw : piecePrice;
  const vat = item.vat_code ?? item.vat ?? "";
  const vatRate = item.vat_rate ?? "";
  return { boxPrice, piecePrice, offerBox, offerPiece, vat, vatRate };
};

const isRhpTenant = () =>
  config.PREFIX === "rhp" ||
  localStorage.getItem("accessToken") === "rhp" ||
  localStorage.getItem("accessToken") === "hp";

const AddReturnRhp = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [noteInfo, setNoteInfo] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerData, setCustomerData] = useState([]);
  const [returnTableRows, setReturnTableRows] = useState([]);
  const accessToken = localStorage.getItem("accessToken");
  const token = localStorage.getItem("token");
  const user_id = localStorage.getItem("userId");
  const [productOptions, setProductOptions] = useState([]);
  const [productValue, setProductValue] = useState(null);
  const [productInputValue, setProductInputValue] = useState("");
  const [teleCallerOptions, setTeleCallerOptions] = useState([]);
  const [selectedTeleCaller, setSelectedTeleCaller] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const customerSearchTimerRef = useRef(null);
  const productCatalogRef = useRef([]);
  const productOptionsRef = useRef([]);
  const productListRequestRef = useRef(0);

  useEffect(() => {
    if (!isRhpTenant()) {
      navigate("/return/add_return", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const fetchTeleCallerOptions = async () => {
      try {
        const response = await fetch(`${config.API_URL}/getteleSales`, {
          method: "POST",
          headers: {
            authorization: `Bearer ${accessToken}`,
          },
        });
        const data = await response.json();
        setTeleCallerOptions(
          Array.isArray(data.data)
            ? data.data.map((item) => ({
                value: item.id,
                label: `${item.first_name || ""} ${item.last_name || ""}`.trim(),
              }))
            : []
        );
      } catch (error) {
        setTeleCallerOptions([]);
      }
    };
    fetchTeleCallerOptions();
  }, [accessToken]);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(format(date, "dd-MM-yyyy"));
    } else {
      setSelectedDate("");
    }
  };

  const getDateValue = () => {
    if (!selectedDate) return null;
    try {
      return parse(selectedDate, "dd-MM-yyyy", new Date());
    } catch {
      return null;
    }
  };

  const filterProductOptions = useCallback((inputValue) => {
    const query = String(inputValue ?? "").trim().toLowerCase();
    const allOptions = productOptionsRef.current;
    if (!query) {
      setProductOptions(allOptions.slice(0, PRODUCT_OPTION_INITIAL));
      return;
    }
    const filtered = allOptions.filter((option) =>
      (option.label || "").toLowerCase().includes(query)
    );
    setProductOptions(filtered.slice(0, PRODUCT_OPTION_LIMIT));
  }, []);

  const handleCustomerInputChange = (inputValue) => {
    if (customerSearchTimerRef.current) {
      clearTimeout(customerSearchTimerRef.current);
    }
    const trimmed = String(inputValue ?? "").trim();
    if (trimmed.length < 2) {
      setCustomerData([]);
      return;
    }
    customerSearchTimerRef.current = setTimeout(() => {
      fetchCustomerNameOptions(trimmed);
    }, 300);
  };

  const fetchCustomerNameOptions = async (inputValue) => {
    if (inputValue.length < 2) return;
    const formData = new FormData();
    formData.append("pattern", inputValue);
    formData.append("platform", "web");
    formData.append("authorization", accessToken);
    try {
      const response = await fetch(`${config.API_URL}/ws-get-customer-list`, {
        method: "POST",
        headers: {},
        body: formData,
      });
      const data = await response.json();
      setCustomerData(
        Array.isArray(data.customer_details)
          ? data.customer_details.map((item) => ({
              label: item.name,
              value: item.id,
            }))
          : []
      );
    } catch (error) {
      toast.error("Error fetching customer options");
    }
  };

  const fetchProductList = async (selectedOption) => {
    const requestId = productListRequestRef.current + 1;
    productListRequestRef.current = requestId;
    const formData = new FormData();
    formData.append("platform", "web");
    formData.append("authorization", `Bearer ${accessToken}`);
    formData.append("customer_id", selectedOption.value);
    try {
      const response = await axios.post(
        `${config.API_URL}/get_return_productslist`,
        formData,
        {}
      );
      if (productListRequestRef.current !== requestId) return;
      const details = Array.isArray(response.data?.product_details)
        ? response.data.product_details
        : [];
      productCatalogRef.current = details.map((item) => {
        const prices = resolveProductPrices(item);
        return {
          id: item.id,
          product_id: item.product_id ?? item.id,
          product_name: item.product_name,
          product_code: item.product_code,
          order_type: item.order_type || item.product_type || "Box",
          split: normalizeSplit(item.split),
          product_price_type: item.product_price_type,
          box_price: prices.boxPrice,
          piece_price: prices.piecePrice,
          offer_box_price: prices.offerBox,
          offer_piece_price: prices.offerPiece,
          vat: prices.vat,
          vat_rate: prices.vatRate,
        };
      });
      const formattedOptions = productCatalogRef.current.map((item) => ({
        label: `${item.product_name || "N/A"} - ${item.product_code || "N/A"}`,
        value: String(item.product_id),
      }));
      productOptionsRef.current = formattedOptions;
      setProductOptions(formattedOptions.slice(0, PRODUCT_OPTION_INITIAL));
    } catch (error) {
      if (productListRequestRef.current === requestId) {
        toast.error("Error fetching products data");
      }
    }
  };

  const handleCustomerChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedCustomer(selectedOption);
      productCatalogRef.current = [];
      productOptionsRef.current = [];
      setProductOptions([]);
      setProductValue(null);
      setProductInputValue("");
      setReturnTableRows([]);
      fetchProductList(selectedOption);
    } else {
      setSelectedCustomer(null);
      setProductOptions([]);
      setProductValue(null);
      setProductInputValue("");
      productCatalogRef.current = [];
      productOptionsRef.current = [];
      setReturnTableRows([]);
    }
  };

  const addProductToReturnList = (productId, productLabel = "") => {
    if (!selectedCustomer?.value) {
      toast.error("Please select a customer.");
      return;
    }
    const catalogItem = productCatalogRef.current.find(
      (item) => String(item.product_id) === String(productId)
    );
    if (!catalogItem) {
      toast.error(
        productLabel
          ? `Product not found: ${productLabel}`
          : "Product not found"
      );
      return;
    }
    const alreadyAdded = returnTableRows.some(
      (row) => String(row.product_id) === String(catalogItem.product_id)
    );
    if (alreadyAdded) {
      toast.error("Product already added to return list");
      setProductValue(null);
      setProductInputValue("");
      return;
    }
    const returnType = defaultReturnTypeFromSplit(catalogItem.split);
    const priceFields = pricesForReturnType(catalogItem, returnType);
    setReturnTableRows((prev) => [
      ...prev,
      {
        id: `local-${catalogItem.product_id}-${Date.now()}`,
        product_id: catalogItem.product_id,
        product_code: catalogItem.product_code,
        product_name: catalogItem.product_name,
        order_type: catalogItem.order_type,
        split: catalogItem.split,
        box_price: catalogItem.box_price,
        piece_price: catalogItem.piece_price,
        offer_box_price: catalogItem.offer_box_price,
        offer_piece_price: catalogItem.offer_piece_price,
        return_qty: 1,
        return_type: returnType,
        std_price: priceFields.std_price,
        offer_price: priceFields.offer_price,
        vat: catalogItem.vat,
        returnReason: "",
        item_discount: "0",
        item_discount_type: "percentage",
      },
    ]);
    setProductValue(null);
    setProductInputValue("");
    toast.success("Product added to return list");
  };

  const productHandleChange = (selectedOption) => {
    if (selectedOption) {
      setProductValue(selectedOption);
      setProductInputValue(selectedOption.label || "");
      setProductOptions((prev) => {
        const exists = prev.some(
          (o) => String(o.value) === String(selectedOption.value)
        );
        if (exists) return prev;
        return [selectedOption, ...prev].slice(0, PRODUCT_OPTION_LIMIT);
      });
      addProductToReturnList(selectedOption.value, selectedOption.label);
    } else {
      setProductValue(null);
      setProductInputValue("");
      setProductOptions(productOptionsRef.current.slice(0, PRODUCT_OPTION_INITIAL));
    }
  };

  const updateReturnRow = (rowId, patch) => {
    setReturnTableRows((prev) =>
      prev.map((row) => (String(row.id) === String(rowId) ? { ...row, ...patch } : row))
    );
  };

  const handleReturnTypeChange = (rowId, nextType) => {
    const returnType = normalizeReturnUnit(nextType);
    setReturnTableRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(rowId)) return row;
        const priceFields = pricesForReturnType(row, returnType);
        const updatedRow = {
          ...row,
          return_type: returnType,
          order_type: displayOrderType(returnType),
          ...priceFields,
        };
        const unitPrice = parseMoney(updatedRow.offer_price);
        const discount = parseMoney(updatedRow.item_discount);
        const discountType = normalizeDiscountType(updatedRow.item_discount_type);
        const discountInvalid =
          discount > 0 &&
          ((discountType === "percentage" && discount > 100) ||
            (discountType === "amount" && unitPrice > 0 && discount > unitPrice));
        return discountInvalid
          ? { ...updatedRow, item_discount: "0" }
          : updatedRow;
      })
    );
  };

  const handleDiscountValueChange = (rowId, value) => {
    setReturnTableRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(rowId)) return row;
        return { ...row, item_discount: value };
      })
    );
  };

  const handleDiscountValueBlur = (row) => {
    const raw = String(row.item_discount ?? "").trim();
    if (raw === "" || raw === ".") {
      updateReturnRow(row.id, { item_discount: "0" });
      return;
    }

    const parsed = parseMoney(raw);
    const formatted = parsed.toFixed(2);

    if (!validateRowDiscount(row, formatted, row.item_discount_type)) {
      updateReturnRow(row.id, { item_discount: "0" });
      return;
    }

    updateReturnRow(row.id, { item_discount: formatted });
  };

  const handleDiscountTypeChange = (rowId, nextType) => {
    setReturnTableRows((prev) =>
      prev.map((row) => {
        if (String(row.id) !== String(rowId)) return row;
        const discountType = normalizeDiscountType(nextType);
        const currentValue = String(row.item_discount ?? "0");
        if (!validateRowDiscount(row, currentValue, discountType)) {
          return { ...row, item_discount: "0", item_discount_type: discountType };
        }
        return { ...row, item_discount_type: discountType };
      })
    );
  };

  const deleteReturnRecord = (rowId) => {
    setConfirmDialog({
      open: true,
      title: "Are you sure?",
      message: "Do you want to delete this return record?",
      onConfirm: () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        setReturnTableRows((prev) =>
          prev.filter((row) => String(row.id) !== String(rowId))
        );
        toast.success("The return record has been deleted.");
      },
    });
  };

  const SubmitRejectFormData = () => {
    if (!selectedDate || returnTableRows.length === 0) {
      toast.error("Please select a date and add products to return.");
      return;
    }
    if (!String(noteInfo ?? "").trim()) {
      toast.error("Please enter a note.");
      return;
    }
    for (const row of returnTableRows) {
      const qty = parseFloat(row.return_qty);
      if (!Number.isFinite(qty) || qty < 0) {
        toast.error("Please enter return quantity for all products.");
        return;
      }
      if (!row.returnReason) {
        toast.error("Please select a reason for all products.");
        return;
      }
    }

    setConfirmDialog({
      open: true,
      title: "Are you sure?",
      message: "Do you want to submit this return?",
      onConfirm: async () => {
        setConfirmDialog({ open: false, title: "", message: "", onConfirm: null });
        const formData = {
          return_date: selectedDate,
          customerId: selectedCustomer.value,
          note: noteInfo,
          user_id: user_id,
          returnProductDetails: returnTableRows.map((row) => ({
            itemId: String(row.product_id),
            productId: String(row.product_id),
            returnPieceType:
              row.return_type || normalizeReturnUnit(row.order_type),
            returnQty: String(row.return_qty),
            returnReason: row.returnReason || "",
            item_discount: String(parseMoney(row.item_discount).toFixed(2)),
            item_discount_type: normalizeDiscountType(row.item_discount_type),
          })),
        };
        try {
          const response = await fetch(`${config.API_URL}/return_request_web`, {
            method: "POST",
            headers: {
              authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(formData),
          });
          if (!response.ok) throw new Error("Failed to submit return");
          await response.json();
          toast.success("Return data submitted successfully!");
          setSelectedDate("");
          setNoteInfo("");
          setReturnTableRows([]);
          navigate("/return_list");
        } catch (error) {
          toast.error("Something went wrong. Please try again.");
        }
      },
    });
  };

  if (!isRhpTenant()) {
    return null;
  }

  return (
    <Box>
      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px: 2, py: 1.2 }}>
            <form id="addReturnRhpForm">
              <Grid2 container spacing={1} alignItems="center">
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Typography className="page-title">Add Return</Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      className="input-border"
                      label="Date"
                      value={getDateValue()}
                      onChange={handleDateChange}
                      format="dd-MM-yyyy"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: {
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f5f7fa",
                              fontSize: "0.875rem",
                              "&:hover": { backgroundColor: "#fff" },
                              "&.Mui-focused": { backgroundColor: "#fff" },
                            },
                            "& .MuiInputLabel-root": {
                              color: "#000",
                              fontSize: "0.875rem",
                            },
                            "& .MuiFormLabel-root": { top: "-3px" },
                            "& .MuiFormLabel-root.Mui-focused": { top: "0px" },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <Autocomplete
                    className="input-border"
                    options={customerData}
                    value={selectedCustomer}
                    onChange={(event, newValue) => handleCustomerChange(newValue)}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "reset") return;
                      handleCustomerInputChange(newInputValue);
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Customer"
                        size="small"
                        className="input-border"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 2 }}>
                  <Autocomplete
                    className="input-border"
                    options={productOptions}
                    value={productValue}
                    inputValue={productInputValue}
                    onInputChange={(event, newInputValue, reason) => {
                      if (reason === "reset") {
                        setProductInputValue(newInputValue);
                        return;
                      }
                      if (reason === "clear") {
                        setProductValue(null);
                        setProductInputValue("");
                        setProductOptions(
                          productOptionsRef.current.slice(0, PRODUCT_OPTION_INITIAL)
                        );
                        return;
                      }
                      setProductInputValue(newInputValue);
                      filterProductOptions(newInputValue);
                    }}
                    onChange={(event, newValue) => productHandleChange(newValue)}
                    onOpen={() => {
                      if (
                        productOptionsRef.current.length &&
                        productOptions.length === 0
                      ) {
                        setProductOptions(
                          productOptionsRef.current.slice(0, PRODUCT_OPTION_INITIAL)
                        );
                      }
                    }}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      String(option?.value) === String(value?.value)
                    }
                    filterOptions={(options) => options}
                    ListboxProps={{ style: { maxHeight: 280 } }}
                    disabled={!selectedCustomer}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Product"
                        size="small"
                        className="input-border"
                      />
                    )}
                  />
                </Grid2>
                <Grid2 size={{ xs: 12, md: 3 }}>
                  <Autocomplete
                    className="input-border"
                    options={teleCallerOptions}
                    value={selectedTeleCaller}
                    onChange={(event, newValue) => setSelectedTeleCaller(newValue)}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?.value === value?.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Tele Caller"
                        size="small"
                        className="input-border"
                      />
                    )}
                  />
                </Grid2>
              </Grid2>

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mt: 2,
                  fontSize: "0.875rem",
                  color: "#212b36",
                }}
              >
                Return Items
              </Typography>
              <TableContainer
                sx={{
                  mb: 3,
                  maxHeight: 400,
                  border: "1px solid rgba(145, 158, 171, 0.16)",
                  borderRadius: 1,
                }}
              >
                <Table
                  size="medium"
                  className="table-cell-border"
                  sx={{ tableLayout: "fixed" }}
                >
                  <TableHead>
                    <TableRow className="table-cell-header">
                      <TableCell sx={{ width: 50, textAlign: "center", padding: "4px" }}>
                        No
                      </TableCell>
                      <TableCell sx={{ width: 100, padding: "4px" }}>Code</TableCell>
                      <TableCell sx={{ width: 300, padding: "4px" }}>
                        Product Description
                      </TableCell>
                      <TableCell sx={{ width: 50, padding: "4px" }}>Type</TableCell>
                      <TableCell sx={{ width: 120, textAlign: "center", padding: "4px" }}>
                        QTY
                      </TableCell>
                      <TableCell sx={{ width: 90, textAlign: "right", padding: "4px" }}>
                        Std. Price
                      </TableCell>
                      <TableCell sx={{ width: 90, textAlign: "right", padding: "4px" }}>
                        Offer Price
                      </TableCell>
                      <TableCell sx={{ width: 90, textAlign: "center", padding: "4px" }}>
                        Item Disc
                      </TableCell>
                      <TableCell sx={{ width: 70, textAlign: "right", padding: "4px" }}>
                        Total
                      </TableCell>
                      <TableCell sx={{ width: 60, textAlign: "right", padding: "4px" }}>
                        VAT
                      </TableCell>
                      <TableCell sx={{ width: 140, padding: "4px" }}>Return Type</TableCell>
                      <TableCell sx={{ width: 40, textAlign: "center", padding: "4px" }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {returnTableRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                          <Typography
                            variant="body2"
                            sx={{ color: "#637381", fontSize: "0.75rem" }}
                          >
                            No product
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      returnTableRows.map((row, index) => {
                        const total = calculateRowTotal(row);
                        const discountType = normalizeDiscountType(row.item_discount_type);
                        return (
                        <TableRow
                          key={row.id}
                          hover
                          className="table-cell-style"
                        >
                          <TableCell sx={{ textAlign: "center", padding: "4px" }}>
                            <Typography fontSize="0.7rem" fontWeight={500}>
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ padding: "4px" }}>
                            <Typography fontSize="0.7rem" fontWeight={500}>
                              {row.product_code || ""}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ padding: "4px" }}>
                            <Typography fontSize="0.7rem" fontWeight={700}>
                              {row.product_name || ""}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ padding: "4px" }}>
                            {normalizeSplit(row.split) === "2" ? (
                              <FormControl size="small" fullWidth>
                                <MuiSelect
                                  className="input-border"
                                  value={normalizeReturnUnit(row.return_type)}
                                  onChange={(e) =>
                                    handleReturnTypeChange(row.id, e.target.value)
                                  }
                                  sx={{
                                    fontSize: "0.75rem",
                                    "& .MuiSelect-select": { fontSize: "0.75rem" },
                                  }}
                                >
                                  <MenuItem value="box" sx={{ fontSize: "0.75rem" }}>
                                    Box
                                  </MenuItem>
                                  <MenuItem value="piece" sx={{ fontSize: "0.75rem" }}>
                                    Piece
                                  </MenuItem>
                                </MuiSelect>
                              </FormControl>
                            ) : (
                              <Typography fontSize="0.7rem">
                                {displayOrderType(row.return_type || row.order_type)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: "center", padding: "4px" }}>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                              <QuantityControl
                                value={Number(row.return_qty) || 1}
                                onChange={(newQty) => {
                                  const unitPrice = parseMoney(row.offer_price);
                                  const discount = parseMoney(row.item_discount);
                                  const discountType = normalizeDiscountType(
                                    row.item_discount_type
                                  );
                                  const discountInvalid =
                                    discount > 0 &&
                                    ((discountType === "percentage" && discount > 100) ||
                                      (discountType === "amount" &&
                                        unitPrice > 0 &&
                                        discount > unitPrice));
                                  updateReturnRow(row.id, {
                                    return_qty: newQty,
                                    ...(discountInvalid ? { item_discount: "0" } : {}),
                                  });
                                }}
                                min={1}
                                max={9999}
                                size="small"
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ textAlign: "right", padding: "4px" }}>
                            <Typography fontSize="0.7rem">
                              {formatMoney(row.std_price)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "right", padding: "4px" }}>
                            <Typography fontSize="0.7rem">
                              {formatMoney(row.offer_price)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center", padding: "4px" }}>
                            <TextField
                              size="small"
                              value={row.item_discount ?? "0"}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || value === ".") {
                                  handleDiscountValueChange(row.id, value);
                                  return;
                                }
                                if (discountType === "percentage") {
                                  if (/^\d*\.?\d{0,2}$/.test(value)) {
                                    handleDiscountValueChange(row.id, value);
                                  }
                                  return;
                                }
                                if (/^\d*\.?\d{0,2}$/.test(value)) {
                                  handleDiscountValueChange(row.id, value);
                                }
                              }}
                              onBlur={() => handleDiscountValueBlur(row)}
                              type="number"
                              placeholder="0"
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    <MuiSelect
                                      value={discountType}
                                      onChange={(e) =>
                                        handleDiscountTypeChange(row.id, e.target.value)
                                      }
                                      size="small"
                                      sx={{
                                        height: 12,
                                        fontSize: "0.7rem",
                                        minWidth: 32,
                                        width: 32,
                                        "& .MuiSelect-select": {
                                          py: 0,
                                          px: 0,
                                          fontSize: "0.7rem",
                                          minHeight: "auto",
                                          textAlign: "center",
                                          textOverflow: "clip",
                                          overflow: "visible",
                                        },
                                        "& .MuiSelect-icon": {
                                          fontSize: "0.55rem",
                                          right: 5,
                                        },
                                        "& fieldset": {
                                          border: "none",
                                        },
                                      }}
                                    >
                                      <MenuItem
                                        value="amount"
                                        sx={{
                                          fontSize: "0.7rem",
                                          px: 0.75,
                                          justifyContent: "center",
                                        }}
                                      >
                                        £
                                      </MenuItem>
                                      <MenuItem
                                        value="percentage"
                                        sx={{
                                          fontSize: "0.7rem",
                                          px: 0.75,
                                          justifyContent: "center",
                                        }}
                                      >
                                        %
                                      </MenuItem>
                                    </MuiSelect>
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{
                                step: "0.01",
                                min: "0",
                                max: discountType === "percentage" ? "100" : undefined,
                                style: {
                                  textAlign: "right",
                                  fontSize: "0.75rem",
                                  padding: "4px 4px",
                                },
                              }}
                              sx={{
                                minWidth: 70,
                                width: 90,
                                "& .MuiInputBase-root": {
                                  height: 26,
                                  fontSize: "0.7rem",
                                },
                                "& .MuiOutlinedInput-root": {
                                  padding: 0,
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#000",
                                },
                                "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                                  {
                                    opacity: 1,
                                  },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ textAlign: "right", padding: "4px" }}>
                            <Typography fontSize="0.7rem">
                              {formatMoney(total)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: "right", padding: "4px" }}>
                            <Typography fontSize="0.7rem">
                              {row.vat === "" || row.vat == null ? "-" : row.vat}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ padding: "4px" }}>
                            <FormControl size="small" fullWidth>
                              <MuiSelect
                                className="input-border"
                                value={row.returnReason ?? ""}
                                onChange={(e) =>
                                  updateReturnRow(row.id, {
                                    returnReason: e.target.value,
                                  })
                                }
                                displayEmpty
                                sx={{
                                  fontSize: "0.75rem",
                                  "& .MuiSelect-select": { fontSize: "0.75rem" },
                                }}
                              >
                                <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                                  Select
                                </MenuItem>
                                <MenuItem
                                  value="return"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                  Return
                                </MenuItem>
                                <MenuItem
                                  value="short"
                                  sx={{ fontSize: "0.75rem" }}
                                >
                                 Short
                                </MenuItem>
                                
                              </MuiSelect>
                            </FormControl>
                          </TableCell>
                          <TableCell sx={{ textAlign: "center", padding: "4px" }}>
                            <IconButton
                              size="small"
                              onClick={() => deleteReturnRecord(row.id)}
                              sx={{ color: "error.main" }}
                            >
                              <Iconify
                                icon="mdi:close"
                                width={18}
                                height={18}
                              />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Grid2 container spacing={2} sx={{ mb: 1 }}>
                <Grid2 size={{ xs: 6 }}>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    required
                    label="Note"
                    multiline
                    rows={2}
                    value={noteInfo}
                    onChange={(e) => setNoteInfo(e.target.value)}
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": { fontSize: "0.875rem" },
                      "& .MuiInputLabel-root": { fontSize: "0.875rem" },
                    }}
                  />
                </Grid2>
                <Grid2 size={{ xs: 6 }}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      className="button-variant-contained"
                      onClick={SubmitRejectFormData}
                      size="small"
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid2>
              </Grid2>
            </form>
          </Box>
        </Paper>
      </Box>

      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          })
        }
      >
        <DialogTitle sx={{ fontSize: "1.125rem", fontWeight: 600 }}>
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem", color: "#637381" }}>
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                title: "",
                message: "",
                onConfirm: null,
              })
            }
            color="inherit"
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (confirmDialog.onConfirm) confirmDialog.onConfirm();
            }}
            variant="contained"
            className="button-variant-contained"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddReturnRhp;
